import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { apiRequest } from '../../utils/apiClient';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import ResultCard from '../search/components/ResultCard';
import { toFrontendProperty, type SearchProperty } from '../../services/ragSearchService';
import type { Property } from '../../mocks/properties';

const PAGE_SIZE = 24;

interface PublicCompany {
  id: string;
  name: string;
  nameAr?: string | null;
  logoUrl?: string | null;
  coverUrl?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  region?: string | null;
  regionAr?: string | null;
  districtName?: string | null;
  districtNameAr?: string | null;
  isVerified: boolean;
  avgRating: number;
  totalReviews: number;
  activeListingsCount: number;
  soldCount: number;
  rentedCount: number;
  createdAt?: string;
}

interface ListMeta { totalItems: number; totalPages: number }

export default function CompanyProfilePage() {
  const { i18n } = useTranslation(['common']);
  const isRTL = i18n.dir() === 'rtl';
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<PublicCompany | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [listings, setListings] = useState<Property[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listingsLoading, setListingsLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    apiRequest<{ data: PublicCompany }>(`/companies/${slug}`)
      .then((res) => setCompany(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const fetchListings = useCallback(async (companyId: string, p: number) => {
    setListingsLoading(true);
    try {
      const res = await apiRequest<{ data: SearchProperty[]; meta?: ListMeta }>(
        `/properties?companyId=${companyId}&page=${p}&pageSize=${PAGE_SIZE}`
      );
      setListings((res.data ?? []).map(toFrontendProperty) as Property[]);
      setTotalPages(res.meta?.totalPages ?? 1);
    } catch {
      setListings([]);
      setTotalPages(1);
    } finally {
      setListingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (company?.id) fetchListings(company.id, page);
  }, [company?.id, page, fetchListings]);

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== page) { setPage(next); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !company) {
    return (
      <div className="min-h-screen bg-white" dir={i18n.dir()}>
        <Navigation />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] mb-4">
            {isRTL ? 'الشركة غير موجودة' : 'Company not found'}
          </h1>
          <Link to="/trusted-brokers">
            <Button variant="primary">{isRTL ? 'تصفح الشركات' : 'Browse companies'}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const name = isRTL ? (company.nameAr || company.name) : company.name;
  const description = isRTL ? (company.descriptionAr || company.description) : company.description;
  const city = isRTL ? (company.regionAr || company.region) : company.region;
  const district = isRTL ? (company.districtNameAr || company.districtName) : company.districtName;
  const joined = company.createdAt ? new Date(company.createdAt).getFullYear() : null;
  const waPhone = (company.phone || '').replace(/[^0-9]/g, '');

  const stats = [
    { label: isRTL ? 'إعلانات' : 'Listings', value: company.activeListingsCount, icon: 'ri-building-line' },
    { label: isRTL ? 'مبيعات' : 'Sold', value: company.soldCount, icon: 'ri-price-tag-3-line' },
    { label: isRTL ? 'إيجارات' : 'Rented', value: company.rentedCount, icon: 'ri-key-line' },
    { label: isRTL ? 'التقييم' : 'Rating', value: company.totalReviews > 0 ? company.avgRating.toFixed(1) : '-', icon: 'ri-star-line' },
  ];

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={i18n.dir()}>
      <Navigation />
      <main className="pb-20">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header card */}
          <section className="rounded-3xl bg-white border border-slate-200/80 overflow-hidden mb-4 shadow-sm">
            <div className="h-24 bg-[#004D40]" />
            <div className="px-5 sm:px-8 pb-6">
              <div className={`flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 ${isRTL ? 'sm:flex-row-reverse sm:text-right' : ''}`}>
                {/* Logo */}
                <div className="shrink-0">
                  {company.logoUrl ? (
                    <img src={company.logoUrl} alt={name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white shadow-md object-cover bg-white" />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl ring-4 ring-white shadow-md bg-[#004D40] flex items-center justify-center">
                      <span className="text-white text-3xl font-bold">{name.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0 pt-2 sm:pt-0">
                  <div className={`flex items-center gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <h1 className="text-xl sm:text-2xl font-bold text-[var(--pv-color-primary)] truncate">{name}</h1>
                    {company.isVerified && <i className="ri-verified-badge-fill text-[#004D40] text-xl" title={isRTL ? 'موثّق' : 'Verified'} />}
                  </div>
                  <div className={`flex items-center flex-wrap gap-3 mt-2.5 text-xs text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {company.totalReviews > 0 && (
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400" />
                        <span className="font-semibold text-slate-700">{company.avgRating.toFixed(1)}</span>
                        <span>({company.totalReviews})</span>
                      </span>
                    )}
                    {city && (
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-2-line" />{city}{district ? `, ${district}` : ''}
                      </span>
                    )}
                    {joined && (
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />{isRTL ? `منضم منذ ${joined}` : `Joined ${joined}`}
                      </span>
                    )}
                  </div>
                </div>

                {/* Contact actions */}
                <div className={`flex items-center gap-2 shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {company.phone && (
                    <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#004D40] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm">
                      <i className="ri-whatsapp-line" /><span className="hidden sm:inline">{isRTL ? 'واتساب' : 'WhatsApp'}</span>
                    </a>
                  )}
                  {company.phone && (
                    <a href={`tel:${company.phone}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
                      <i className="ri-phone-line" /><span className="hidden sm:inline">{isRTL ? 'اتصال' : 'Call'}</span>
                    </a>
                  )}
                  {company.email && (
                    <a href={`mailto:${company.email}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
                      <i className="ri-mail-line" /><span className="hidden sm:inline">{isRTL ? 'إيميل' : 'Email'}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {stats.map((s) => (
              <div key={s.label} className={`rounded-2xl bg-white border border-slate-200/80 p-4 ${isRTL ? 'text-right' : ''}`}>
                <div className={`flex items-center gap-2 text-xs text-slate-400 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <i className={s.icon} />{s.label}
                </div>
                <div className="text-xl font-semibold text-slate-900">{s.value}</div>
              </div>
            ))}
          </section>

          {description && <p className={`text-gray-600 mb-6 leading-relaxed ${isRTL ? 'text-right' : ''}`}>{description}</p>}

          {/* Listings */}
          <h2 className={`text-xl font-bold text-[var(--pv-color-primary)] mb-4 ${isRTL ? 'text-right' : ''}`}>{isRTL ? 'العقارات' : 'Listings'}</h2>
          {listingsLoading ? (
            <div className="flex justify-center py-12"><GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" /></div>
          ) : listings.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200 text-gray-500">
              {isRTL ? 'لا توجد عقارات نشطة' : 'No active listings'}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map((p, index) => (
                <ResultCard key={p.id} property={p} eagerImage={index < 3} onSelect={(item) => navigate(`/property/${item.id}`)} />
              ))}
            </div>
          )}

          {/* Pager */}
          {!listingsLoading && totalPages > 1 && (
            <div className={`mt-8 flex flex-wrap items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">{isRTL ? 'السابق' : 'Prev'}</button>
              {(() => {
                const nums: number[] = [];
                const start = Math.max(1, page - 2);
                const end = Math.min(totalPages, page + 2);
                for (let i = start; i <= end; i++) nums.push(i);
                return nums.map((n) => (
                  <button key={n} onClick={() => goToPage(n)} className={`min-w-[2.5rem] px-3 py-2 rounded-lg border text-sm font-medium ${n === page ? 'bg-[#004D40] border-[#004D40] text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{n}</button>
                ));
              })()}
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">{isRTL ? 'التالي' : 'Next'}</button>
              <span className={`text-sm text-gray-500 ${isRTL ? 'mr-3' : 'ml-3'}`}>{isRTL ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}</span>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
